import { mergeConversationPatch } from "@/lib/conversation-patch";
import type { Conversation, ConversationPatch } from "@/types/chat";

/** Chats the queue shows. The API has no count for this filter. */
export const ATTENTION_QUEUE_LIMIT = 5;
/** One more than it shows, so "there are more" is known rather than guessed
 *  from a cursor (which comes back whenever a page is exactly full). */
export const ATTENTION_QUEUE_FETCH_LIMIT = ATTENTION_QUEUE_LIMIT + 1;

/** The cached page: up to `ATTENTION_QUEUE_FETCH_LIMIT` chats, newest first. */
export interface AttentionQueueData {
  items: Conversation[];
}

export type AttentionPatchResult =
  | { kind: "unchanged" }
  | { kind: "updated"; data: AttentionQueueData }
  /** The patch can't be applied exactly; read the queue again. */
  | { kind: "refetch" };

function sentAt(conversation: Conversation): number {
  // Patches deliver ISO strings at runtime even where the type says Date.
  const sendAt = conversation.lastMessage?.sendAt;
  return sendAt ? new Date(sendAt).getTime() : 0;
}

/** Same order as the server's needs-attention index: latest message first. */
function newestFirst(items: Conversation[]): Conversation[] {
  return [...items].sort((a, b) => sentAt(b) - sentAt(a));
}

/**
 * Apply a conversation patch from the event stream to the cached queue.
 *
 * Updates and removals are exact. A chat joining the queue needs fields a
 * patch doesn't carry, and leaving a full page may uncover one the cache
 * never read, so both ask for a refetch, as does a patch that doesn't say
 * whether the chat is flagged.
 */
export function applyAttentionPatch(data: AttentionQueueData, patch: ConversationPatch): AttentionPatchResult {
  if (patch.needsAttention === undefined) return { kind: "refetch" };
  const index = data.items.findIndex((c) => c.id === patch.id);

  if (!patch.needsAttention) {
    if (index === -1) return { kind: "unchanged" };
    if (data.items.length > ATTENTION_QUEUE_LIMIT) return { kind: "refetch" };
    return { kind: "updated", data: { items: data.items.filter((c) => c.id !== patch.id) } };
  }

  if (index === -1) return { kind: "refetch" };
  const items = data.items.map((c, i) => (i === index ? mergeConversationPatch(c, patch) : c));
  return { kind: "updated", data: { items: newestFirst(items) } };
}
